<!-- The app's own dialog, because Ionic's could not be persuaded to be one.
     `app.css` still carries a long block of `ion-alert` custom properties
     written to fix exactly this, and measured in the built app it did not: the
     wrapper stayed rgb(255,255,255), the non-destructive buttons stayed
     rgb(59,130,246) in caps, the message stayed Ionic's grey, and the buttons
     stayed 40px tall. Those properties are real, but they set the values that
     Ionic's own shadow-part rules then set again, later and more specifically,
     and a custom property cannot outrank a declaration that reads it.

     That would be a cosmetic argument anywhere else in the app. It is not
     here, because the two dialogs that matter most in this product are alerts:

       "Delete Bolognaise?"  the only irreversible thing the app does, about a
                             recipe that may be the last of someone's cooking.
       "That recording is    read at the moment someone tapped to hear a dead
        no longer on this    relative's voice and the voice did not come.
        device…"

     Both of those were being delivered as a white system card with a blue
     KEEP IT in capitals, floating on a sheet of exercise paper. The sentence
     inside had been written and rewritten; the thing carrying it had not been
     designed at all.

     So this stops fighting Ionic and draws the dialog: paper, ink, a serif,
     one bottom rule under anything you type into, and buttons big enough for
     a wet hand. It teleports to <body> for the same reason Ionic's overlays
     do — inside `ion-content` it would be trapped in that element's scroll
     and stacking context — and because it is outside `.hd-page`, NOTHING it
     inherits can be assumed. Every foreground colour below is stated. That
     includes the focus ring: `.hd-page :focus-visible` cannot reach here.

     The prop and button shapes deliberately mirror `IonAlert`, down to a
     handler that receives `{ [name]: value }` and can return `false` to keep
     the dialog open, so swapping a call site is a tag name and one event
     name and nothing else. -->
<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';

/* One id per instance, for aria-labelledby. A page holds several of these at
   once — the recipe sheet has seven — and once teleported they are all in
   <body> together, so the id has to be unique per instance rather than per
   component. Random rather than a counter: everything at the top level of
   `<script setup>` runs once per instance, so a module counter declared here
   would be reset to zero by each of them. */
const uid = `hd-dialog-${Math.random().toString(36).slice(2, 9)}`;

const props = defineProps({
  /** Open or not. Renders nothing when false. */
  open: { type: Boolean, default: false },
  /** The question, in the serif. Also the dialog's accessible name. */
  header: { type: String, default: '' },
  /** One quiet line under the header — "It sits beside this step". */
  subHeader: { type: String, default: '' },
  /** The body prose. This is where the hard sentences live. */
  message: { type: String, default: '' },
  /**
   * [{ name, type: 'text'|'number'|'textarea', value, placeholder, min, rows,
   *    label }]
   *
   * The subset of IonAlert's `inputs` this app actually uses. `rows` is also
   * read from `attributes.rows`, because that is the form IonAlert documents
   * and the form two call sites already pass.
   *
   * `label` is new and optional: a placeholder is not an accessible name —
   * it vanishes the moment someone types — so an unlabelled field is named
   * from placeholder or `name` as a fallback, and any field given a `label`
   * gets a real visible one.
   */
  inputs: { type: Array, default: () => [] },
  /**
   * [{ text, role?: 'cancel'|'destructive', handler?: (data) => void }], or a
   * plain string for a button that only closes the dialog.
   */
  buttons: { type: Array, default: () => [] },
});

/* TWO WAYS TO CLOSE IT, and both fire on every path — backdrop, Escape, and
   any button. `update:open` makes `v-model:open` work; `dismiss` is the
   one-word swap for the `@didDismiss` the call sites are already written
   against. Use whichever suits; do not use both on one call site, or the
   state gets set to false twice, which is harmless but reads as a mistake. */
const emit = defineEmits(['update:open', 'dismiss']);

const panel = ref(null);

const fields = computed(() =>
  props.inputs.map((input, i) => ({
    key: input.name ?? String(i),
    type: ['number', 'textarea', 'select'].includes(input.type) ? input.type : 'text',
    options: Array.isArray(input.options) ? input.options : [],
    label: input.label ?? '',
    placeholder: input.placeholder ?? '',
    min: input.min,
    rows: Number(input.rows ?? input.attributes?.rows) || 3,
    /* Placeholder before `name`: "What is it called?" is a better name for a
       field than "title", and it is the only description anyone wrote. */
    name: input.label || input.placeholder || input.name || 'Field',
  }))
);

const actions = computed(() =>
  props.buttons
    .map((b) => (typeof b === 'string' ? { text: b } : b))
    .filter((b) => b && b.text)
);

/* Three or more and they stack full width. A row of three on a 390px phone
   gives each one about 110px with the text shrunk to fit it, which is a row
   of targets you aim at rather than press. The ORDER is the caller's and is
   never rearranged — "Remove / Cancel / Save" reads oddly stacked, but a
   component that silently reorders someone's buttons is worse than one that
   lays them out plainly. */
const stacked = computed(() => actions.value.length > 2);

/* What is typed, keyed exactly as the handler will receive it. */
const values = ref({});

/* SEEDED ON OPEN, AND ONLY ON OPEN. Several callers pass a computed `inputs`
   derived from the store — `ingredientInputs` rebuilds whenever any
   ingredient changes — so re-seeding whenever the prop changed identity
   would wipe half-typed words out from under someone the moment anything
   else in the app wrote to IndexedDB. The values are correct at open because
   the caller sets what is being edited and `open` in the same tick. */
function seed() {
  const next = {};
  props.inputs.forEach((input, i) => {
    next[input.name ?? String(i)] = input.value ?? '';
  });
  values.value = next;
}

function close() {
  emit('update:open', false);
  emit('dismiss');
}

function press(button) {
  /* A COPY of the values, and IonAlert's `false` contract: a handler that
     returns false keeps the dialog open, which is how a call site validates
     without the field it is validating disappearing first. Nothing in the app
     uses it yet; the alternative is the first caller that needs it having to
     come and change this file. */
  const outcome = typeof button.handler === 'function' ? button.handler({ ...values.value }) : undefined;
  if (outcome === false) return;
  close();
}

/* Backdrop and Escape mean "the cancel button", when there is one — so a
   cancel handler that does cleanup still runs, and so the two gestures cannot
   mean something different from the button beside them. With no cancel button
   they just close, which is right for the one-button dialogs ("Close"). */
function dismiss() {
  const cancel = actions.value.find((b) => b.role === 'cancel');
  if (cancel) press(cancel);
  else close();
}

/* Enough for what this dialog can contain. `[tabindex]:not([tabindex="-1"])`
   catches nothing today and costs nothing to keep. */
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

const focusable = () => (panel.value ? Array.from(panel.value.querySelectorAll(FOCUSABLE)) : []);

function onKeydown(event) {
  if (event.key === 'Escape') {
    /* Stopped as well as prevented: Ionic and the router both listen for
       Escape at the document, and one keypress should close one thing. */
    event.preventDefault();
    event.stopPropagation();
    dismiss();
    return;
  }
  if (event.key !== 'Tab') return;

  const items = focusable();
  if (items.length === 0) { event.preventDefault(); return; }

  const first = items[0];
  const last = items[items.length - 1];
  const here = document.activeElement;
  const inside = panel.value?.contains(here);

  if (event.shiftKey && (here === first || !inside)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (here === last || !inside)) {
    event.preventDefault();
    first.focus();
  }
}

/* Where focus came from, so it can be put back. Losing it sends a screen
   reader and a keyboard back to the top of the page, and on this app that
   means back past the whole recipe to the toolbar. */
let returnTo = null;

function restore() {
  const el = returnTo;
  returnTo = null;
  /* isConnected, because the thing that opened the dialog is quite often the
     thing the dialog deleted. */
  if (el && el.isConnected) el.focus();
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) { restore(); return; }
    seed();
    returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    nextTick(() => {
      /* The first field if there is one — that is what someone came to type
         into, and it is what IonAlert does. Otherwise the cancel button, so
         the first thing Enter can reach is the harmless one: this dialog is
         also how a recipe gets deleted. */
      const items = focusable();
      const target = panel.value?.querySelector('input, textarea')
        ?? items.find((el) => el.dataset.role === 'cancel')
        ?? items[0];
      target?.focus();
    });
  },
  { immediate: true }
);

/* A dialog unmounted while open — a route change under it — must not leave
   focus on a detached node. */
onBeforeUnmount(() => { if (props.open) restore(); });
</script>

<template>
  <Teleport to="body">
    <Transition name="rise">
      <div
        v-if="open"
        class="veil"
        @click.self="dismiss"
        @keydown="onKeydown"
      >
        <div
          ref="panel"
          class="panel"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="`${uid}-header`"
          :aria-describedby="message ? `${uid}-message` : undefined"
        >
          <div class="scroll">
            <h2 :id="`${uid}-header`" class="header">{{ header }}</h2>
            <p v-if="subHeader" class="sub">{{ subHeader }}</p>
            <p v-if="message" :id="`${uid}-message`" class="message">{{ message }}</p>

            <div v-if="fields.length" class="fields">
              <label v-for="field in fields" :key="field.key" class="field">
                <span v-if="field.label" class="field-label">{{ field.label }}</span>
                <textarea
                  v-if="field.type === 'textarea'"
                  v-model="values[field.key]"
                  :rows="field.rows"
                  :placeholder="field.placeholder"
                  :aria-label="field.label ? undefined : field.name"
                ></textarea>
                <select
                  v-else-if="field.type === 'select'"
                  v-model="values[field.key]"
                  :aria-label="field.label ? undefined : field.name"
                >
                  <option v-for="opt in field.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
                <input
                  v-else
                  v-model="values[field.key]"
                  :type="field.type"
                  :min="field.min"
                  :placeholder="field.placeholder"
                  :aria-label="field.label ? undefined : field.name"
                >
              </label>
            </div>
          </div>

          <div class="actions" :class="{ stacked }">
            <button
              v-for="(button, i) in actions"
              :key="`${button.text}-${i}`"
              type="button"
              class="action"
              :class="[button.role === 'destructive' && 'destructive', button.role === 'cancel' && 'cancel hd-card']"
              :data-role="button.role || 'default'"
              @click="press(button)"
            >
              {{ button.text }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* 20000 is where Ionic starts its overlays, and each one it opens takes
   20000 + its index. Sitting exactly on the floor puts this above every page,
   header and footer in the app, and below a toast — which is the order you
   want the one time both are on screen. */
.veil {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px max(20px, env(safe-area-inset-bottom, 0px));
  background: color-mix(in srgb, var(--hd-ink) 45%, transparent);
}

/* 2px, like everything else here. The only rounded thing in this app is the
   24px play disc, and it is round because it is a disc. */
.panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 380px;
  max-height: 100%;
  border-radius: 2px;
  background: var(--hd-sheet);
  border: 1px solid color-mix(in srgb, var(--hd-ink) 9%, transparent);
  /* Wide and low — a sheet lifted a little off the page, rather than the
     tight dark blur that reads as system chrome. */
  box-shadow: 0 20px 46px -20px color-mix(in srgb, var(--hd-ink) 50%, transparent),
              0 2px 8px color-mix(in srgb, var(--hd-ink) 10%, transparent);
}

/* The buttons stay put; a long message scrolls. Nothing in this app has a
   message that long, but "That recording is no longer on this device…" on a
   small phone in the largest text size is close. */
.scroll {
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 20px 20px 4px;
}

.header {
  margin: 0;
  font-family: var(--hd-serif);
  font-size: var(--hd-t-heading);
  font-weight: 600;
  line-height: 1.25;
  color: var(--hd-ink);
}

.sub {
  margin: 4px 0 0;
  font-family: var(--hd-serif);
  font-style: italic;
  font-size: var(--hd-t-small);
  line-height: 1.4;
  color: var(--hd-pencil);
}

/* Generous, because this is prose and some of it is bad news. */
.message {
  margin: 10px 0 0;
  font-family: var(--hd-serif);
  font-size: var(--hd-t-body);
  line-height: 1.55;
  color: var(--hd-pencil);
}

.fields {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 14px;
}

.field {
  display: block;
}

.field-label {
  display: block;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-small);
  letter-spacing: 0.01em;
  color: var(--hd-pencil);
}

/* A LINE YOU WRITE ON, not a box you fill in. The whole app is a page, and a
   grey Material field with a fill and four rounded corners is the one shape
   that could not be on one. So: no background, no border but the rule under
   it, the serif the rest of the writing is in, and the margin's red for the
   caret — the same red that means "she is speaking" everywhere else.

   line-height is the 28px rule pitch, so the words sit on the line exactly as
   they do on the sheet; the padding takes the target to 44px without moving
   them off it. */
.field input,
.field select,
.field textarea {
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 8px 0;
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--hd-feint);
  border-radius: 0;
  font-family: var(--hd-serif);
  font-size: var(--hd-t-body);
  line-height: var(--hd-rule);
  color: var(--hd-ink);
  caret-color: var(--hd-margin);
  -webkit-appearance: none;
  appearance: none;
}

.field textarea {
  resize: vertical;
  min-height: calc(var(--hd-rule) * 2);
}

.field input::placeholder,
.field textarea::placeholder {
  color: color-mix(in srgb, var(--hd-pencil) 62%, transparent);
  opacity: 1; /* Firefox dims placeholders again on top of the colour. */
}

/* The rule itself is the focus indicator: 2px in the focus teal, the full
   width of the field. An outline box around a field whose whole design is
   "there is no box" would undo the field at the moment you use it, and a 2px
   change of colour and weight across the field's width is a stronger
   indicator than the 1px hairline most outlines draw. Never red — red is
   recording. */
.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
  border-bottom: 2px solid var(--hd-focus);
  padding-bottom: 7px;
}

.actions {
  display: flex;
  gap: 8px;
  padding: 14px 20px max(14px, env(safe-area-inset-bottom, 0px));
}

.actions .action { flex: 1 1 0; }

/* Three or more: full width each, stacked, so every one of them is a target
   the width of the dialog and nobody has to aim. */
.actions.stacked {
  flex-direction: column;
}

/* 44px is the floor both Apple and Google publish, and Ionic was shipping
   these at 40. NOT UPPERCASE: this app writes "Keep it", the way a person
   writes it, and Material's caps turned the gentlest button in the product
   into KEEP IT. */
.action {
  min-height: 44px;
  padding: 10px 14px;
  border: 0;
  border-radius: 2px;
  background: transparent;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-control);
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
  color: var(--hd-ink);
  cursor: pointer;
}

/* `.hd-card` in the class list gives this the paper-and-hairline surface the
   shelf's rows use; only the text colour has to be said again, because
   nothing outside `.hd-page` inherits ink. */
/* THE PAPER HAS TO BE RESTATED HERE, and the reason is a specificity tie that
   is easy to recreate. The button also carries `.hd-card` from app.css, which
   supplies its background — but the scoped `.action { background: transparent }`
   above compiles to `.action[data-v-…]`, which is (0,2,0) and outranks
   `.hd-card` at (0,1,0). So the cancel button rendered as a HOLE: hairline and
   inner highlight, no paper, showing whatever was behind the dialog. */
.action.cancel {
  background: var(--hd-sheet);
  color: var(--hd-ink);
}

/* Filled, because this is the one button in the app that cannot be undone and
   it should not look like the others. Red stays red here for the reason
   app.css gives: destructive red is what red means in every OS, and this is
   the one place the app's single red does double duty. */
.action.destructive {
  background: var(--hd-margin);
  color: var(--hd-sheet);
}

/* Said here rather than inherited: the `.hd-page :focus-visible` rule cannot
   reach a teleported element, and without this the ring is whatever the
   platform draws — which on Android is the blue this component exists to get
   rid of. */
.action:focus-visible {
  outline: 2px solid var(--hd-focus);
  outline-offset: 2px;
}

/* A short fade and 8px of rise: paper being set down, not a card springing
   in. The backdrop crossfades with it — a backdrop that arrives first reads
   as two events. */
.rise-enter-active,
.rise-leave-active {
  transition: opacity 180ms ease;
}
.rise-enter-active .panel,
.rise-leave-active .panel {
  transition: opacity 180ms ease, transform 180ms cubic-bezier(0.32, 0.72, 0, 1);
}
.rise-enter-from,
.rise-leave-to {
  opacity: 0;
}
.rise-enter-from .panel,
.rise-leave-to .panel {
  opacity: 0;
  transform: translateY(8px);
}
/* Still leaving, no longer clickable. */
.rise-leave-active {
  pointer-events: none;
}

/* The travel goes, the crossfade stays — WCAG 2.3.3 asks for the first and
   not the second, and a dialog that appears with no transition at all is
   indistinguishable from a mis-tap. */
@media (prefers-reduced-motion: reduce) {
  .rise-enter-from .panel,
  .rise-leave-to .panel {
    transform: none;
  }
}
</style>
